import { extendObservable, runInAction } from 'mobx';
import jwtDecode from 'jwt-decode';

import { ApiHttpService, ApiHttpServiceSecondary, ApiHttpServiceNewApi } from '../networking/ApiHttpService';
import { SessionController } from '../networking/controllers/SessionController';
import { ApiError, ERROR_STATUSES, ERROR_CODES } from '../models/ApiError';
import { USER_TYPES, User } from '../models/User';
import { Logger } from '../helpers/Logger';
import { Storage } from '../helpers/Storage';
import { Recaptcha } from '../helpers/Recaptcha';
import { PasswordConfirmationStore } from './PasswordConfirmationStore';

/*
  This store handles all session-related logic.
*/

const TOKEN_KEY = 'token';
const REFRESH_TOKEN_KEY = 'refreshToken';
const USER_LOGGED_KEY = 'user_logged';
const TOKENNEWAPI_KEY = 'token_newapi';

const ONE_MINUTE = 60 * 1000;
const MAX_RENEWAL_RETRIES = 3;
const MINIMUM_RENEWAL_INTERVAL = ONE_MINUTE;
const STORAGE_POLL_INTERVAL = 5 * 1000;
const TEN_SECONDS = 10 * 1000;
const RENEWAL_PROCESS_RETRY_TIME = TEN_SECONDS;

class SessionStoreClass {
  static generateRegisterData(userType, data) {
    const dataToSend = {};
    if (userType === USER_TYPES.COMPANY) {
      dataToSend.fictitiousName = data.fictitiousName;
      dataToSend.rut = data.rut;
      dataToSend.tradeName = data.tradeName;
    } else if (userType === USER_TYPES.PERSON) {
      dataToSend.names = data.names;
      dataToSend.personalId = data.personalId;
      dataToSend.selectedCountry = data.selectedCountry;
      dataToSend.surnames = data.surnames;
    } else if (userType === USER_TYPES.UNIPERSONAL) {
      dataToSend.names = data.names;
      dataToSend.personalId = data.personalId;
      dataToSend.rut = data.rut;
      dataToSend.selectedCountry = data.selectedCountry;
      dataToSend.surnames = data.surnames;
    } else {
      return null;
    }
    return {
      ...dataToSend,
      email: data.email,
      password: data.password,
      recaptchaToken: data.recaptchaToken,
    };
  }

  static generateUpdateData(data) {
    const dataToSend = {};
    if (data.type === USER_TYPES.COMPANY) {
      dataToSend.fictitiousName = data.fictitiousName;
      dataToSend.tradeName = data.tradeName;
    } else if (data.type === USER_TYPES.PERSON) {
      dataToSend.names = data.names;
      dataToSend.document = data.document;
      dataToSend.documentCountry = data.documentCountry;
      dataToSend.surnames = data.surnames;
    } else if (data.type === USER_TYPES.UNIPERSONAL) {
      dataToSend.names = data.names;
      dataToSend.document = data.document;
      dataToSend.rut = data.rut;
      dataToSend.documentCountry = data.documentCountry;
      dataToSend.surnames = data.surnames;
    } else {
      return null;
    }
    return {
      ...dataToSend,
      id: data.id,
      email: data.email,
    };
  }

  constructor() {
    this.renewalTimeoutId = null;
    this.renewalRetrials = 0;
    this.renewalInProgress = false;
    this.renewalCallbacks = [];
    this.renewalFailureCallbacks = [];
    this._getToken = this._getToken.bind(this);
    this._getTokenNewApi = this._getTokenNewApi.bind(this);
    this._updateTokensFromStorage = this._updateTokensFromStorage.bind(this);

    const token = Storage.secureGet(TOKEN_KEY) || null;
    const tokenNewApi = Storage.secureGet(TOKENNEWAPI_KEY) || null;
    const refreshToken = Storage.secureGet(REFRESH_TOKEN_KEY) || null;
    const userData = Storage.secureGet(USER_LOGGED_KEY);
    const user = userData ? new User(userData) : null;
    extendObservable(this, {
      internalError: false,
      editPasswordError: false,
      editPasswordInternalError: false,
      error: false,
      resetToken: 0,
      haveToResetPassword: false,
      registerAuthError: false,
      registerError: false,
      registerApiError: null,
      registerInternalError: false,
      updateError: false,
      updateSuccess: false,
      cancelError: false,
      user,
      socialNetworks: {},
      token,
      tokenNewApi,
      refreshToken,
      get loggedIn() {
        return this.user !== null && this.token !== null && this.refreshToken !== null;
      },
    });
    if (token && refreshToken) {
      this._handleTokenExpiration(token, refreshToken);
    }
    // Set token callback on every request
    ApiHttpService.onAuth(this._getToken);
    ApiHttpServiceSecondary.onAuth(this._getToken);
    ApiHttpServiceNewApi.onAuth(this._getTokenNewApi);
    // Set polling mechanism to check the storage for tokens
    window.setInterval(this._updateTokensFromStorage, STORAGE_POLL_INTERVAL);
  }

  /*
    This function is used to set the token on every
    authenticated request on ApiHttpService.
    If the token is being renewed, it returns a promise
    that resolves once the token has successfully been renewed
    or fails if renewal has failed.
  */
  _getToken() {
    if (!this.loggedIn) {
      return undefined;
    }
    if (!this.renewalInProgress) {
      // Fetch token from the storage to ensure validity
      const token = Storage.secureGet(TOKEN_KEY);
      if (!token) {
        // The user has logged out on another tab.
        this.logout();
        return undefined;
      }
      return `token:${token}`;
    }
    return new Promise((resolve, reject) => {
      this.renewalCallbacks.push(token => resolve(`token:${token}`));
      this.renewalFailureCallbacks.push(reject);
    });
  }

  _getTokenNewApi() {
    if (!this.loggedIn) {
      return undefined;
    }
    const token = Storage.secureGet(TOKENNEWAPI_KEY);
    if (!token) {
      // logout
      return undefined;
    }
    return `Bearer ${token}`;
  }

  /*
    Decodes the given token, reads its expiry date and determines
    when the token should be renewed.
    If it is already expired, it is instantly renewed.
    If not, then a timeout is set to renew it some time before its expiry.
  */
  _handleTokenExpiration(token, refreshToken) {
    const decodedToken = jwtDecode(token);
    // We artificially make the token expire one minute earlier to allow renovation
    // without disturbing the user experience.
    const expiry = new Date((decodedToken.exp * 1000) - ONE_MINUTE);
    const currentMillisecondsFromEpoch = (new Date()).valueOf();
    const timeToExpiry = expiry.valueOf() - currentMillisecondsFromEpoch;
    if (timeToExpiry <= 0) {
      Logger.log('Token has expired');
      this._renewToken(refreshToken);
    } else {
      // Set a callback to renew, a random time before expiry is selected to reduce
      // the chance of multiple tabs renewing the token at the same time.
      let randomTime = 0;
      if (timeToExpiry > MINIMUM_RENEWAL_INTERVAL) {
        randomTime = Math.round((Math.random() * (timeToExpiry - MINIMUM_RENEWAL_INTERVAL))
          + MINIMUM_RENEWAL_INTERVAL);
      }
      // Unset current timeout event if it exists
      if (this.renewalTimeoutId) {
        window.clearTimeout(this.renewalTimeoutId);
      }
      this.renewalTimeoutId = window.setTimeout(() => {
        this._renewToken(this.refreshToken);
      }, timeToExpiry - randomTime);
      const renewalDate = new Date(currentMillisecondsFromEpoch + (timeToExpiry - randomTime));
      Logger.log(`Token set to be renewed at ${renewalDate}`);
    }
  }

  /*
    Renews the token using `refreshToken`. If renewal fails it is
    retried a certain amount of times before giving up and try again after a period of time.
  */
  async _renewToken(refreshToken) {
    try {
      const storedToken = Storage.get(TOKEN_KEY);
      if (!storedToken) {
        // If there's no token on storage, then the user has logged out at some point.
        this.logout();
        return;
      } else if (this.token !== storedToken) {
        // If the stored token exists and is not the one in memory, then
        // avoid renewing it and use that one.
        // Also, the refresh token is fetched as well in case the user has logged out and
        // in again in the span of renewal.
        runInAction(() => {
          this.token = storedToken;
          this.refreshToken = Storage.get(REFRESH_TOKEN_KEY);
          this.user = Storage.get(USER_LOGGED_KEY);
        });
        Logger.log('Found different token on storage when renewing, using that one instead.');
        this._handleTokenExpiration(this.token, this.refreshToken);
        return;
      }
      this.renewalInProgress = true;
      const { data: newToken } = await SessionController.renewToken(refreshToken);
      // Check token on storage in case the user has logged out on another tab
      if (!Storage.get(TOKEN_KEY)) {
        // Fail all requests that are waiting for the token
        this.renewalFailureCallbacks.forEach(callback => callback(new Error('User logged out on another tab while renewal was in progress')));
        this.renewalFailureCallbacks = [];
        this.logout();
        return;
      }
      runInAction(() => {
        this.token = newToken;
      });
      Logger.log('Token renewed successfully');
      this._handleTokenExpiration(this.token, refreshToken);
      // Save new token
      Storage.secureSet(TOKEN_KEY, this.token);
      // Unblock all requests waiting for the new token
      this.renewalCallbacks.forEach(callback => callback(this.token));
      this.renewalCallbacks = [];
      this.renewalInProgress = false;
      this.renewalRetrials = 0;
    } catch (error) {
      Logger.warn(`Failed to renew token, retried ${this.renewalRetrials} times, error was: ${error.message}`);
      if (error.response && error.response.status === ERROR_STATUSES.UNAUTHORIZED) {
        this.renewalFailureCallbacks.forEach(callback => callback(new Error('Failed to get new token')));
        this.renewalFailureCallbacks = [];
        this.renewalInProgress = false;
        this.logout();
      } else if (this.renewalRetrials < MAX_RENEWAL_RETRIES) {
        this.renewalRetrials += 1;
        this._renewToken(this.refreshToken);
      } else {
        // Set a timeout to wait before starting the renewal token process again
        this.renewalTimeoutId = window.setTimeout(() => {
          this._renewToken(this.refreshToken);
        }, RENEWAL_PROCESS_RETRY_TIME);
        // Reset number of retrials
        this.renewalRetrials = 0;
        const currentMillisecondsFromEpoch = (new Date()).valueOf();
        const renewalDate = new Date(currentMillisecondsFromEpoch + RENEWAL_PROCESS_RETRY_TIME);
        Logger.log(`Renewal token process set to start again at ${renewalDate}`);
      }
    }
  }

  _updateTokensFromStorage() {
    const storedToken = Storage.get(TOKEN_KEY);
    const storedRefreshToken = Storage.get(REFRESH_TOKEN_KEY);
    if (this.loggedIn && (!storedToken || !storedRefreshToken)) {
      // Fail all requests that are waiting for the token
      this.renewalFailureCallbacks.forEach(callback => callback(new Error('User logged out on another tab while renewal was in progress')));
      this.renewalFailureCallbacks = [];
      Logger.log('Stored token is null, logging out.');
      this.logout();
      return;
    }
    if (storedToken && storedToken !== this.token) {
      const user = Storage.get(USER_LOGGED_KEY);
      runInAction(() => {
        this.token = storedToken;
        this.refreshToken = storedRefreshToken;
        this.user = user;
      });
      Logger.log('Found different token in storage. Updating now.');
      this._handleTokenExpiration(storedToken, storedRefreshToken);
    }
  }

  _logUserIn(token, refreshToken, user, tokenNewApi) {
    Storage.secureSet(TOKEN_KEY, token);
    Storage.secureSet(REFRESH_TOKEN_KEY, refreshToken);
    Storage.secureSet(USER_LOGGED_KEY, user);
    Storage.secureSet(TOKENNEWAPI_KEY, tokenNewApi);
    runInAction(() => {
      this.token = token;
      this.refreshToken = refreshToken;
      this.user = user;
      this.tokenNewApi = tokenNewApi;
    });
  }

  async login(email, password) {
    try {
      runInAction(() => {
        this.error = false;
        this.internalError = false;
      });
      const [apiLogin, newApiLogin] = await Promise.allSettled([
        SessionController.login(email, password),
        SessionController.loginNewApi(email, password),
      ]);
      if (apiLogin.status === 'fulfilled' && newApiLogin.status === 'fulfilled') {
        const { token, refreshToken, user } = apiLogin.value;
        const { tokenNewApi } = newApiLogin.value;
        this._logUserIn(token, refreshToken, user, tokenNewApi);
        this._handleTokenExpiration(token, refreshToken);
      } else {
        if (apiLogin.status !== 'fulfilled') {
          throw new ApiError(apiLogin.reason);
        }
        throw new Error(newApiLogin.reason);
      }
    } catch (error) {
      Logger.warn(error);
      if (ApiError.isSameTypeAs(error)) {
        if (error.status >= ERROR_STATUSES.SERVER_ERROR) {
          runInAction(() => {
            this.internalError = true;
          });
        } else if (
          error.code === ERROR_CODES.INVALID_LOGIN ||
          error.code === ERROR_CODES.ACTUAL_PASSWORD_INCORRECT ||
          error.code === ERROR_CODES.ACCOUNT_BLOCKED
        ) {
          runInAction(() => {
            this.error = error;
          });
        } else if (error.code === ERROR_CODES.MUST_RESET_PASSWORD) {
          runInAction(() => {
            this.haveToResetPassword = true;
            this.resetToken = error.content;
          });
        } else {
          Logger.error(error);
          runInAction(() => {
            this.internalError = true;
          });
        }
      } else {
        runInAction(() => {
          this.registerAuthError = true;
        });
      }
    }
  }

  async getUser() {
    try {
      const [user, socialNetworks] = await Promise.allSettled([
        SessionController.getUser(),
        SessionController.getSocialNetworksNewApi(),
      ]);
      if (user.status === 'fulfilled') {
        runInAction(() => {
          this.user = user.value;
        });
      }
      if (socialNetworks.status === 'fulfilled') {
        runInAction(() => {
          this.socialNetworks = socialNetworks.value;
        });
      }
      Storage.secureSet(USER_LOGGED_KEY, this.user);
    } catch (error) {
      Logger.warn(error);
    }
  }

  async updateUser(data, dataSocialNetworks = []) {
    runInAction(() => {
      this.updateSuccess = false;
      this.cancelError = false;
    });
    const dataToSend = SessionStoreClass.generateUpdateData(data);
    if (!dataToSend) {
      return;
    }
    try {
      const [user, socialNetworks] = await Promise.allSettled([
        data.type === USER_TYPES.COMPANY ? SessionController.updateCompany(dataToSend) :
          SessionController.updatePerson(dataToSend),
        SessionController.saveSocialNetworksNewApi(dataSocialNetworks),
      ]);
      if (user.status === 'fulfilled') {
        runInAction(() => {
          this.user = user.value;
          this.updateError = false;
          this.updateSuccess = true;
        });
        Storage.secureSet(USER_LOGGED_KEY, this.user);
      }
      if (socialNetworks.status === 'fulfilled') {
        runInAction(() => {
          this.socialNetworks = socialNetworks.value;
        });
      }
    } catch (error) {
      Logger.warn(error);
      if (error.response && error.response.status === ERROR_STATUSES.PRECONDITION_FAILED) {
        // Wait for confirmation and try to submit again when done
        try {
          await PasswordConfirmationStore.showConfirmationModal(() => this.updateUser(data));
        } catch (err) {
          runInAction(() => {
            this.cancelError = true;
          });
        }
      } else {
        runInAction(() => {
          this.updateError = true;
        });
      }
    }
  }

  clearUpdateSuccess() {
    runInAction(() => {
      this.updateSuccess = false;
    });
  }

  async editPassword(data) {
    try {
      await SessionController.editPassword(data);
      runInAction(() => {
        this.editPasswordError = false;
        this.editPasswordInternalError = false;
      });
    } catch (error) {
      Logger.warn(error);
      if (ApiError.isSameTypeAs(error)) {
        if (error.status >= ERROR_STATUSES.SERVER_ERROR) {
          runInAction(() => {
            this.editPasswordInternalError = true;
          });
        } else if (error.code === ERROR_CODES.ACTUAL_PASSWORD_INCORRECT) {
          runInAction(() => {
            this.editPasswordError = true;
          });
        } else {
          Logger.error(error);
          runInAction(() => {
            this.editPasswordInternalError = true;
          });
        }
      }
    }
  }

  resetEditUserErrors() {
    runInAction(() => {
      this.updateError = false;
      this.cancelError = false;
      this.editPasswordError = false;
      this.editPasswordInternalError = false;
    });
  }

  logout() {
    runInAction(() => {
      this.token = null;
      this.tokenNewApi = null;
      this.refreshToken = null;
      this.user = null;
    });
    Storage.remove(TOKEN_KEY);
    Storage.remove(TOKENNEWAPI_KEY);
    Storage.remove(REFRESH_TOKEN_KEY);
    Storage.remove(USER_LOGGED_KEY);
    // If a renewal callback has been set, delete it
    if (this.renewalTimeoutId) {
      window.clearTimeout(this.renewalTimeoutId);
      this.renewalTimeoutId = null;
    }
  }

  async register(userType, data) {
    const dataToSend = SessionStoreClass.generateRegisterData(userType, data);
    if (!dataToSend) {
      return;
    }
    try {
      let response = null;
      if (userType === USER_TYPES.COMPANY) {
        response = await SessionController.registerCompany(dataToSend);
      } else {
        response = await SessionController.registerPerson(dataToSend);
      }
      try {
        const { tokenNewApi } = await SessionController.loginNewApi(data.email, data.password);
        const { token, refreshToken, user } = response;
        this._logUserIn(token, refreshToken, user, tokenNewApi);
        runInAction(() => {
          this.registerError = false;
          this.registerApiError = null;
          this.registerInternalError = false;
        });
        this._handleTokenExpiration(token, refreshToken);
      } catch (error) {
        throw new Error(error.message);
      }
    } catch (error) {
      Logger.warn(error);
      if (ApiError.isSameTypeAs(error)) {
        if (error.status >= ERROR_STATUSES.SERVER_ERROR) {
          runInAction(() => {
            this.registerInternalError = true;
          });
        } else if (error.code === ERROR_CODES.INVALID_RUT ||
            error.code === ERROR_CODES.CLIENT_ALREADY_EXISTS ||
            error.code === ERROR_CODES.INVALID_URUGUAYAN_DOCUMENT) {
          runInAction(() => {
            this.registerApiError = error.code;
          });
        } else {
          Logger.error(error);
          runInAction(() => {
            this.registerError = true;
          });
        }
      } else {
        Logger.error(error);
        runInAction(() => {
          this.registerAuthError = true;
        });
      }
    } finally {
      Recaptcha.reset();
    }
  }

  resetRegisterErrors() {
    runInAction(() => {
      this.registerError = false;
      this.registerApiError = null;
      this.registerInternalError = false;
      this.haveToResetPassword = false;
    });
  }
}

const SessionStore = new SessionStoreClass();

export { SessionStore };



// WEBPACK FOOTER //
// ./src/stores/SessionStore.js